/**
 * Dashboard widgets: what an answer adds to the session's canvas, what a saved dashboard stores in
 * DOC (definitions only, never rows), and the CSV a widget downloads as. No React, no I/O.
 */
import type { AnalystBlock, AnalystColumn, AnalystMetricTile } from '@circuitry-ai/doc-ui';

export type WidgetKind = 'metric' | 'chart' | 'grid';

/** Which governed query made the widget: Show SQL reads it, Refresh re-runs it. */
export interface WidgetSource {
  tool: 'run_sql' | 'query_metric';
  arguments: Record<string, unknown>;
  query_id: string;
  /** The dashboard's filters it was built with; a refresh without filters re-applies them. */
  dashboard_filters?: DashboardFilters | null;
}

// -- Filters (cty-analytics-agents DESIGN §18; cty-ai-data-access ADR 0004) --------------------

export const PERIOD_PRESETS = [
  'last_7_days', 'last_30_days', 'last_90_days', 'last_12_months', 'this_month', 'last_month',
  'this_quarter', 'last_quarter', 'this_year', 'last_year',
] as const;
export type PeriodPreset = typeof PERIOD_PRESETS[number];

/** A preset, or start and end dates (YYYY-MM-DD, inclusive). `null` in DashboardFilters means all dates. */
export interface FilterPeriod {
  preset?: PeriodPreset;
  start?: string;
  end?: string;
}

/** `column IN values`, on a column the viewer sees as it is. */
export interface FilterCondition {
  column: string;
  values: Array<string | number | boolean>;
}

/** The dashboard's filters: the data access layer applies them inside every dataset a query reads. */
export interface DashboardFilters {
  period?: FilterPeriod | null;
  where?: FilterCondition[];
}

/** A column the dashboard's charts break down by, with the values they show: a filter the bar offers. */
export interface SuggestedFilter {
  column: string;
  values: string[];
}

export const MAX_SUGGESTED_FILTERS = 5;
const MAX_SUGGESTED_VALUES = 50;

/** A widget as the runtime sends it with an answer (POST /insight-agent, /preview). */
export interface InsightWidget {
  id: string;
  title: string;
  kind: WidgetKind;
  question: string;
  block: AnalystBlock;
  /** Opaque to the UI: sent back unchanged on refresh and save. */
  spec: Record<string, unknown>;
  source: WidgetSource;
  /**
   * Every query the widget reads, when its tiles mix queries (Key findings); `source` is the first.
   * Each tile's spec keeps its query's position here, so save and refresh must carry them all.
   */
  sources?: WidgetSource[];
  /** The dashboard section it sits in; neighbouring widgets with the same section form one. */
  section?: string;
  /** Filters none of its queries could take (a dataset without that column): the card says so. */
  filters_not_applied?: string[];
}

/** A widget on a canvas: its block may be missing (data dropped from storage, or not refreshed yet). */
export interface CanvasWidget extends Omit<InsightWidget, 'block' | 'question'> {
  question?: string;
  block?: AnalystBlock | null;
  /** The last refresh failed for this widget. */
  error?: string;
  /** The last refresh ran, but its filters leave this widget nothing to show: not a failure. */
  no_data?: boolean;
}

/** What a saved dashboard keeps of each widget, and what refresh takes. */
export type WidgetDefinition = Pick<CanvasWidget, 'id' | 'title' | 'kind' | 'spec' | 'source' | 'sources' | 'section'>;

export const MAX_WIDGET_ROWS = 200;
export const MAX_REFRESH_WIDGETS = 30;

export const toDefinition = ({ id, title, kind, spec, source, sources, section }: CanvasWidget): WidgetDefinition =>
  ({ id, title, kind, spec, source, ...(sources?.length ? { sources } : {}), ...(section ? { section } : {}) });

/** Every query behind a widget: its `sources` when its tiles mix queries, else its one `source`. */
export const widgetSources = (widget: Pick<CanvasWidget, 'source' | 'sources'>): WidgetSource[] =>
  (widget.sources?.length ? widget.sources : [widget.source]);

/** Rows (chart, grid) or tiles (metric) of a widget's block. */
export function widgetRows(widget: Pick<CanvasWidget, 'block'>): Record<string, unknown>[] {
  const data = widget.block?.payload?.data;
  return Array.isArray(data) ? (data as Record<string, unknown>[]) : [];
}

/** The widget with at most MAX_WIDGET_ROWS rows, for storage. */
export function capRows(widget: CanvasWidget): CanvasWidget {
  const payload = widget.block?.payload;
  if (!payload || !Array.isArray(payload.data) || payload.data.length <= MAX_WIDGET_ROWS) return widget;
  const data = payload.data.slice(0, MAX_WIDGET_ROWS) as typeof payload.data;
  return { ...widget, block: { ...widget.block, payload: { ...payload, data } } };
}

/** What a refresh returned for one widget (POST /insight-agent/dashboards/refresh). */
export interface WidgetRefresh {
  id: string;
  block?: AnalystBlock;
  /** Its queries ran but leave it nothing to show (a narrow filter); `error` then says so for older clients. */
  no_data?: boolean;
  error?: string;
  filters_not_applied?: string[];
}

/**
 * A widget after a refresh: its new block; or no data for these filters, shown as such and not as a
 * failure; or the error, over the numbers it had.
 */
export function refreshed(widget: CanvasWidget, result: WidgetRefresh): CanvasWidget {
  const { filters_not_applied } = result;
  if (result.block) return { ...widget, block: result.block, error: undefined, no_data: undefined, filters_not_applied };
  if (result.no_data) return { ...widget, block: null, error: undefined, no_data: true, filters_not_applied };
  return { ...widget, error: result.error ?? '', no_data: undefined };
}

/** Adds an answer's widgets to a canvas; a widget with the same id is replaced in place. */
export function addWidgets(canvas: CanvasWidget[], incoming: InsightWidget[]): CanvasWidget[] {
  const next = [...canvas];
  for (const widget of incoming) {
    const index = next.findIndex((existing) => existing.id === widget.id);
    if (index >= 0) next[index] = widget;
    else next.push(widget);
  }
  return next;
}

/** Moves a widget one place within its section (it never leaves its section). */
export function moveWidget(canvas: CanvasWidget[], id: string, by: -1 | 1): CanvasWidget[] {
  const from = canvas.findIndex((widget) => widget.id === id);
  const to = from + by;
  if (from < 0 || to < 0 || to >= canvas.length) return canvas;
  if ((canvas[to].section ?? '') !== (canvas[from].section ?? '')) return canvas;
  const next = [...canvas];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}

/** Neighbouring widgets with the same section, in order: the dashboard's sections. */
export function groupSections(canvas: CanvasWidget[]): Array<{ title: string; widgets: CanvasWidget[] }> {
  const sections: Array<{ title: string; widgets: CanvasWidget[] }> = [];
  for (const widget of canvas) {
    const title = widget.section ?? '';
    const last = sections[sections.length - 1];
    if (last?.title === title) last.widgets.push(widget);
    else sections.push({ title, widgets: [widget] });
  }
  return sections;
}

// -- Dashboard mode: one dashboard per conversation ------------------------------------------

/** The dashboard's summary of key findings, when it was written (ISO time), and for which filters. */
export interface DashboardSummary {
  text: string;
  at: string;
  /** The filters it was written for ({} for none); undefined when not known (saved before it was kept). */
  filters?: DashboardFilters;
}

/** The whole dashboard after a request (POST /insight-agent/stream in dashboard mode). */
export interface DashboardUpdate {
  title: string;
  sections: Array<{ title: string; widget_ids: string[] }>;
  /** New and replaced widgets only; a replaced widget keeps its id. */
  widgets: InsightWidget[];
  removed: string[];
  /** The dashboard's filters after the request (the agent may have changed them); null for none. */
  filters?: DashboardFilters | null;
  /** Filters the bar can offer: the columns this update's charts break down by. */
  suggested_filters?: SuggestedFilter[];
  /** A filter change re-ran the widgets without writing a new summary: keep the one there (it says its filters). */
  keep_summary?: boolean;
}

export interface DashboardChange {
  added: number;
  replaced: number;
  removed: number;
}

export const MAX_DASHBOARD_REQUEST_WIDGETS = 30;

/**
 * The dashboard so far, as a dashboard-mode request sends it: definitions and queries, never rows. `spec`
 * lets the service re-run the widgets itself for a filter change asked in chat (AI-9749).
 */
export interface DashboardRequest {
  title: string;
  widgets: Array<{
    id: string; section: string; title: string; kind: WidgetKind; source: WidgetSource; sources?: WidgetSource[];
    spec?: Record<string, unknown>;
  }>;
}

export function dashboardRequest(title: string, canvas: CanvasWidget[]): DashboardRequest {
  return {
    title,
    widgets: canvas.slice(0, MAX_DASHBOARD_REQUEST_WIDGETS).map((widget) => ({
      id: widget.id,
      section: widget.section ?? '',
      title: widget.title,
      kind: widget.kind,
      source: widget.source,
      ...(widget.sources?.length ? { sources: widget.sources } : {}),
      ...(widget.spec ? { spec: widget.spec } : {}),
    })),
  };
}

/** The canvas after an update: the update's order and sections, its new and replaced widgets, minus `removed`. */
export function applyDashboardUpdate(canvas: CanvasWidget[], update: DashboardUpdate): { widgets: CanvasWidget[]; change: DashboardChange } {
  const current = new Map(canvas.map((widget) => [widget.id, widget]));
  const incoming = new Map(update.widgets.map((widget) => [widget.id, widget]));
  const removed = new Set(update.removed);
  const next: CanvasWidget[] = [];
  const placed = new Set<string>();
  for (const section of update.sections) {
    for (const id of section.widget_ids) {
      const widget = incoming.get(id) ?? current.get(id);
      if (!widget || placed.has(id)) continue;
      next.push({ ...widget, section: section.title, error: undefined });
      placed.add(id);
    }
  }
  // Anything the update neither placed nor removed (more widgets than a request sends) stays, at the end.
  for (const widget of canvas) if (!placed.has(widget.id) && !removed.has(widget.id)) next.push(widget);
  const replaced = update.widgets.filter((widget) => current.has(widget.id) && placed.has(widget.id)).length;
  return {
    widgets: next,
    change: {
      added: update.widgets.filter((widget) => !current.has(widget.id) && placed.has(widget.id)).length,
      replaced,
      removed: canvas.filter((widget) => removed.has(widget.id)).length,
    },
  };
}

/** The SQL (run_sql) or the metric call (query_metric) behind a widget, for "Show SQL". */
export function sourceText(source: WidgetSource): string {
  const sql = source.arguments?.sql;
  const text = typeof sql === 'string' ? sql : JSON.stringify({ tool: source.tool, ...source.arguments }, null, 2);
  return source.query_id ? `${text}\n\n-- query_id: ${source.query_id}` : text;
}

// -- CSV -------------------------------------------------------------------------------------

function cellText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value as string | number | boolean | bigint);
}

function csvCell(value: unknown): string {
  let text = cellText(value);
  // A cell starting with = + - @ would run as a formula in a spreadsheet.
  if (/^[=+\-@\t\r]/.test(text) && !/^-?\d+(\.\d+)?$/.test(text)) text = `'${text}`;
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function columnsOf(widget: CanvasWidget, rows: Record<string, unknown>[]): Array<{ field: string; label: string }> {
  const config = widget.block?.payload?.config as { columns?: AnalystColumn[] } | undefined;
  if (widget.kind === 'grid' && Array.isArray(config?.columns) && config.columns.length > 0) {
    return config.columns.map((column) => ({ field: column.field, label: column.label || column.field }));
  }
  const fields: string[] = [];
  for (const row of rows) for (const field of Object.keys(row)) if (!fields.includes(field)) fields.push(field);
  return fields.map((field) => ({ field, label: field }));
}

/** A node of a decomposition tree (AI-9683): the agent nests its query's rows under one root, largest first. */
export interface TreeNode {
  name: string;
  value: number;
  children?: TreeNode[];
}

/** A decomp_tree widget's root and level columns (outermost first), or null for any other widget. */
export function treeOf(widget: Pick<CanvasWidget, 'block'>): { data: TreeNode[]; levels: string[] } | null {
  const payload = widget.block?.payload;
  const config = payload?.config as { chartType?: string; hierarchyFields?: unknown } | undefined;
  if (config?.chartType !== 'decomp_tree' || !Array.isArray(payload?.data)) return null;
  const levels = Array.isArray(config.hierarchyFields) ? config.hierarchyFields.map(String) : [];
  return { data: payload.data as unknown as TreeNode[], levels };
}

/** Reads the rows behind a node of a tree widget (``path``: the chosen value at each level, from the top). */
export type TreeRowsLoader = (widget: CanvasWidget, path: string[]) => Promise<{
  columns: Array<{ field: string; label: string; type?: 'number' | 'text' }>;
  rows: Record<string, unknown>[];
  truncated: boolean;
}>;

/** The tree's leaves as rows, one column per level and the value, as the query returned them. */
export function treeRows(root: TreeNode, levels: string[]): Record<string, unknown>[] {
  const rows: Record<string, unknown>[] = [];
  const walk = (node: TreeNode, depth: number, row: Record<string, unknown>) => {
    if (!node.children?.length || depth >= levels.length) {
      rows.push({ ...row, value: node.value });
      return;
    }
    for (const child of node.children) walk(child, depth + 1, { ...row, [levels[depth]]: child.name });
  };
  walk(root, 0, {});
  return rows;
}

/** CSV of a widget: its rows (chart, grid), its tiles (metric: label, value, format) or a tree's leaves. */
export function widgetCsv(widget: CanvasWidget): string {
  const tree = treeOf(widget);
  if (tree?.data[0] && tree.levels.length > 0) {
    const leaves = treeRows(tree.data[0], tree.levels);
    return [[...tree.levels, 'value'].map(csvCell).join(','),
      ...leaves.map((row) => [...tree.levels, 'value'].map((field) => csvCell(row[field])).join(','))].join('\n');
  }
  const rows = widgetRows(widget);
  if (widget.kind === 'metric') {
    const tiles = rows as unknown as AnalystMetricTile[];
    return ['label,value,format', ...tiles.map((tile) => [tile.label, tile.value, tile.format ?? ''].map(csvCell).join(','))].join('\n');
  }
  const columns = columnsOf(widget, rows);
  return [
    columns.map((column) => csvCell(column.label)).join(','),
    ...rows.map((row) => columns.map((column) => csvCell(row[column.field])).join(',')),
  ].join('\n');
}

function downloadBase(title: string): string {
  const base = title.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean).join('-').slice(0, 60);
  return base || 'widget';
}

export function csvFileName(title: string): string {
  return `${downloadBase(title)}.csv`;
}

export function jpgFileName(title: string): string {
  return `${downloadBase(title)}.jpg`;
}

/** Saves text as a file in the browser. */
export function downloadText(fileName: string, text: string, type = 'text/csv;charset=utf-8'): void {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// -- Saved dashboards (DOC config_name insight_dashboard) ------------------------------------

export type DashboardVisibility = 'private' | 'tenant';

export interface DashboardConfig {
  schema_version: 1;
  title: string;
  description: string;
  agent_id: string | null;
  visibility: DashboardVisibility;
  owner: { sub: string; name: string };
  widgets: WidgetDefinition[];
  /** The agent's summary when it was saved, and its time (ISO). The charts refresh; this text does not. */
  summary?: string;
  summary_at?: string;
  /** The filters the summary was written for. Absent on dashboards saved before it was kept. */
  summary_filters?: DashboardFilters;
  /** The filters it opens with ({} for none). Absent on dashboards saved before filters. */
  filters?: DashboardFilters;
  suggested_filters?: SuggestedFilter[];
}

/** The config DOC stores: definitions only, never blocks or rows. */
export function dashboardConfig(input: {
  title: string;
  description: string;
  agentId: string | null;
  visibility: DashboardVisibility;
  owner: { sub: string; name: string };
  widgets: CanvasWidget[];
  summary?: DashboardSummary;
  /** Undefined: a dashboard from before filters, saved without them. */
  filters?: DashboardFilters | null;
  suggestedFilters?: SuggestedFilter[];
}): DashboardConfig {
  return {
    schema_version: 1,
    title: input.title.trim(),
    description: input.description.trim(),
    agent_id: input.agentId || null,
    visibility: input.visibility,
    owner: input.owner,
    widgets: input.widgets.map(toDefinition),
    ...(input.summary?.text ? {
      summary: input.summary.text,
      summary_at: input.summary.at,
      ...(input.summary.filters ? { summary_filters: input.summary.filters } : {}),
    } : {}),
    ...(input.filters !== undefined ? { filters: input.filters ?? {} } : {}),
    ...(input.suggestedFilters?.length ? { suggested_filters: input.suggestedFilters } : {}),
  };
}

/**
 * The filters a dashboard shows: its own when set; else the snapshot its widgets were built with;
 * else undefined, a dashboard from before filters whose queries carry their own dates.
 */
export function currentFilters(own: DashboardFilters | null | undefined, widgets: Array<Pick<CanvasWidget, 'source' | 'sources'>>): DashboardFilters | undefined {
  if (own !== undefined) return own ?? {};
  for (const widget of widgets) {
    const snapshot = widgetSources(widget).find((source) => source?.dashboard_filters)?.dashboard_filters;
    if (snapshot) return snapshot;
  }
  return undefined;
}

/** The widgets with `filters` as their sources' snapshot, after the filter bar changed them. */
export function withFilters(widgets: CanvasWidget[], filters: DashboardFilters): CanvasWidget[] {
  const mark = (source: WidgetSource) => ({ ...source, dashboard_filters: filters });
  return widgets.map((widget) => ({
    ...widget,
    source: mark(widget.source),
    ...(widget.sources?.length ? { sources: widget.sources.map(mark) } : {}),
  }));
}

/** Suggestions merged by column (case-insensitive), each column's values kept once, in order. */
export function mergeSuggestions(...lists: Array<SuggestedFilter[] | undefined>): SuggestedFilter[] {
  const merged = new Map<string, SuggestedFilter>();
  for (const list of lists) {
    for (const item of list ?? []) {
      const key = item.column.toLowerCase();
      const entry = merged.get(key) ?? { column: item.column, values: [] };
      for (const value of item.values) if (!entry.values.includes(value)) entry.values.push(value);
      entry.values = entry.values.slice(0, MAX_SUGGESTED_VALUES);
      merged.set(key, entry);
    }
  }
  return [...merged.values()].slice(0, MAX_SUGGESTED_FILTERS);
}

/** A saved dashboard's summary, if it has one. */
export function savedSummary(config: Partial<DashboardConfig> | undefined): DashboardSummary | undefined {
  if (!config?.summary || !config.summary_at) return undefined;
  return { text: config.summary, at: config.summary_at, ...(config.summary_filters ? { filters: config.summary_filters } : {}) };
}

/** The same filters, however they are written: no period and null, no conditions and [], any key order. */
export function sameFilters(a: DashboardFilters, b: DashboardFilters): boolean {
  const plain = (filters: DashboardFilters) => JSON.stringify([
    filters.period ? [filters.period.preset ?? '', filters.period.start ?? '', filters.period.end ?? ''] : null,
    (filters.where ?? []).map((condition) => [condition.column.toLowerCase(), [...condition.values].map(String).sort((x, y) => x.localeCompare(y))])
      .sort((x, y) => String(x[0]).localeCompare(String(y[0]))),
  ]);
  return plain(a) === plain(b);
}

/** Filters that narrow the data: a period, or a column condition. */
export const narrows = (filters: DashboardFilters | undefined): boolean =>
  Boolean(filters?.period || filters?.where?.length);

/** Listed for the viewer: their own dashboards and the ones shared with the tenant (UI-only, AI-9616). */
export function visibleTo(config: Partial<DashboardConfig> | undefined, sub: string): boolean {
  return Boolean(config) && (config?.owner?.sub === sub || config?.visibility === 'tenant');
}
